import React from 'react';

import {withRouter, type WithRouterProps} from 'react-router';

import {useAddComment, useComments} from 'app/comments/api';
import {CommentFormWithUploader} from 'app/comments/components/CommentFormWithUploader';
import {CommentList} from 'app/comments/components/CommentList';
import {StickySidebarContentPanel} from 'app/components/general/DetailPage/RightSidebar/structure/StickySidebarContentPanel';
import {StickySidebarMenuButton} from 'app/components/general/DetailPage/RightSidebar/structure/StickySidebarMenuButton';
import NoResultsMessage from 'app/components/general/NoResultsMessage';
import {useCurrentUserState} from 'app/current-user/api';
import {COMMENTS_WIDGET} from 'app/settings/slice';
import {gt} from 'app/utils/i18n';

import type {
  StickyContentPanelType,
  StickyMenuType,
  StickySidebarElementType,
} from 'app/components/general/DetailPage/RightSidebar/structure/types';
import type {DetailPageItemType} from 'app/components/general/DetailPage/types';
import type {AttachedCommentImage, CommentSerializedType} from 'sweet-types';

const BaseStickyDocumentCommentsSidebar = <T extends DetailPageItemType>({
  object,
  version,
  setFullscreenState,
  isFullscreen = false,
  router,
  location,
}: StickyContentPanelType<T> & WithRouterProps) => {
  const [addComment] = useAddComment();

  const user = useCurrentUserState().data!;

  function onAddComment(
    comment: string,
    attachments: Array<AttachedCommentImage>
  ) {
    return addComment({
      object_pk: version.id,
      parentId: object.id,
      content_type: version.content_type,
      comment: comment,
      attachments: {images: attachments},
    })
      .unwrap()
      .then((result: CommentSerializedType) => {
        // append newly added comments id to url for easy highlighting
        const {pathname} = location;
        router.replace({
          pathname,
          hash: `#comment${result?.id}`,
        });
      });
  }

  return (
    <StickySidebarContentPanel
      object={object}
      setFullscreenState={setFullscreenState}
      isFullscreen={isFullscreen}
      bottomActions={
        <CommentFormWithUploader
          className="d-flex justify-content-between"
          buttonClass="btn-primary"
          onSubmit={onAddComment}
          contentType={object.content_type}
          objectId={object.id}
        />
      }
      widgetName={COMMENTS_WIDGET}
    >
      <CommentList
        canDelete={user.is_manager}
        contentType={object.content_type}
        objectId={object.id}
        noResultMessage={
          <NoResultsMessage
            icon="fa-comments"
            head={gt('No comments')}
            onSidebar={true}
          >
            {gt('Use the form below to add a comment')}
          </NoResultsMessage>
        }
      />
    </StickySidebarContentPanel>
  );
};

const StickyCommentsContentPanel = withRouter<
  StickyContentPanelType<DetailPageItemType>
>(BaseStickyDocumentCommentsSidebar);

const StickyCommentsMenuButton = <ItemType extends DetailPageItemType>({
  object,
  buttonDelta,
}: StickyMenuType<ItemType>) => {
  const numComments =
    useComments(
      {
        object_pk: object.id,
        content_type: object.content_type,
        page: 1,
      },
      {refetchOnMountOrArgChange: false}
    ).data?.count ?? 0;

  return (
    <StickySidebarMenuButton
      object={object}
      buttonDelta={buttonDelta}
      widgetName={COMMENTS_WIDGET}
      icon="fa-comments"
      badgeContent={numComments > 0 ? numComments : undefined}
    />
  );
};

export const StickyComments: StickySidebarElementType<DetailPageItemType> = {
  Panel: StickyCommentsContentPanel,
  MenuButton: StickyCommentsMenuButton,
  type: 'right',
  sidebarName: COMMENTS_WIDGET,
};
